import * as React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
    AppBar, Toolbar, Box, Typography, IconButton, Drawer, List, ListItemButton,
    ListItemText, Divider, Container, Stack, useMediaQuery
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme } from "@mui/material/styles";
import { useThemeMode } from "../app/ThemeModeProvider";
import { adminFetch } from "../services/adminFetch";

const drawerWidth = 260;

const navItems = [
    { to: "/admin/dashboard", label: "Dashboard" },
    { to: "/admin/properties", label: "Properties" },
    { to: "/admin/bookings", label: "Bookings" },
    { to: "/admin/pricing", label: "Season Pricing" },
    { to: "/admin/settings", label: "Settings" },
];

export default function AdminLayout() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const { mode, toggle } = useThemeMode();
    const nav = useNavigate();

    const [mobileOpen, setMobileOpen] = React.useState(false);

    const onLogout = async () => {
        try { await adminFetch("/api/admin/auth/logout", { method: "POST" }); } catch { }
        nav("/login");
    };

    const drawer = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontWeight: 1000, letterSpacing: 0.2 }}>
                    RentBoard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Admin Console
                </Typography>
            </Box>
            <Divider />
            <List sx={{ px: 1 }}>
                {navItems.map((it) => (
                    <ListItemButton
                        key={it.to}
                        component={NavLink}
                        to={it.to}
                        sx={{
                            borderRadius: 2,
                            my: 0.5,
                            "&.active": {
                                bgcolor: "action.selected",
                                "& .MuiListItemText-primary": { fontWeight: 900 },
                            },
                        }}
                        onClick={() => setMobileOpen(false)}
                    >
                        <ListItemText primary={it.label} />
                    </ListItemButton>
                ))}
            </List>

            <Box sx={{ flex: 1 }} />
            <Divider />
            <Box sx={{ p: 1.2 }}>
                <ListItemButton onClick={onLogout} sx={{ borderRadius: 2 }}>
                    <LogoutIcon fontSize="small" style={{ marginRight: 10 }} />
                    <ListItemText primary="Logout" />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
            <AppBar
                position="fixed"
                color="default"
                elevation={0}
                sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backdropFilter: "blur(10px)",
                }}
            >
                <Toolbar sx={{ gap: 1 }}>
                    {!isMdUp ? (
                        <IconButton onClick={() => setMobileOpen(true)} edge="start">
                            <MenuIcon />
                        </IconButton>
                    ) : null}

                    <Typography sx={{ fontWeight: 1000, flex: 1 }}>
                        RentBoard
                    </Typography>

                    <IconButton onClick={toggle} aria-label="toggle theme">
                        {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Left drawer */}
            {isMdUp ? (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        [`& .MuiDrawer-paper`]: {
                            width: drawerWidth,
                            boxSizing: "border-box",
                            borderRight: "1px solid",
                            borderColor: "divider",
                        },
                    }}
                >
                    <Toolbar />
                    {drawer}
                </Drawer>
            ) : (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        [`& .MuiDrawer-paper`]: { width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
            )}

            {/* Content */}
            <Box component="main" sx={{ flex: 1 }}>
                <Toolbar />
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Outlet />
                </Container>
            </Box>
        </Box>
    );
}
